import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Alert, CloseAlt } from '@ticketswap/comets'
import flattenDepth from 'lodash/flattenDepth'
import styled from '@emotion/styled'
import {
  useDialog,
  DialogWindow,
  DialogHeader,
  DialogAdornment,
  DialogBody,
  Toggle,
  space,
  H6,
  DialogFooter,
  Button,
  Text,
  color,
  device,
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionPanel,
} from '@ticketswap/solar'
import { decodeGlobalId } from '~/utils/id'
import { externalPrimaryTicketShopFragmentPropTypes } from '~/graphql/fragments/externalPrimaryTicketShop'
import { externalPrimaryTicketShopAlertsPropTypes } from '~/graphql/queries/getExternalPrimaryTicketShopAlerts'

const StyledAccordionItem = styled(AccordionItem)``
const StyledAccordion = styled(Accordion)`
  ${StyledAccordionItem}:first-of-type {
    border-top: unset;

    > button {
      padding-top: 0;
    }
  }

  ${StyledAccordionItem}:last-of-type {
    border-bottom: unset;
  }
`

const EventTypesHeader = styled.div`
  padding: ${space[8]} ${space[16]};
  background-color: ${color.stardust};

  @media ${device.tablet} {
    padding: ${space[8]} ${space[24]};
  }
`

const EventTypeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space[16]};
`

const EventTypesBody = styled.div`
  padding: ${space[16]};

  @media ${device.tablet} {
    padding: ${space[16]} ${space[24]};
  }

  ${EventTypeRow} {
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`

const ExternalPrimaryTicketShopBody = styled(EventTypesBody)`
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
`
const ExternalPrimaryTicketShopText = styled.div`
  span {
    font-size: 14px;
    color: ${color.spaceLight};
  }
`

const ExternalPrimaryTicketShopToggle = styled.div`
  justify-self: end;
`

const StyledDialogBody = styled(DialogBody)`
  padding: 0;

  @media ${device.tablet} {
    padding: 0;
  }
`

function TicketAlertsDialog({
  t,
  alerts,
  externalPrimaryTicketShop,
  eventId,
  onClose,
  externalPrimaryTicketShopAlerts = [],
  ...props
}) {
  const groupedTypes = props.types.groups?.map(
    group => group?.eventTypes?.map(type => ({ node: type })) || []
  )
  const initialTypes = [
    ...(props?.types?.entrance?.length > 0 ? props.types.entrance : []),
    ...(props?.types?.nonEntrance?.length > 0 ? props.types.nonEntrance : []),
    ...flattenDepth(groupedTypes),
  ].map(({ node }) => ({
    id: node.id,
    title: node.title,
    active: getOn(node),
  }))
  const types = useRef(initialTypes)

  const [
    externalPrimaryTicketShopAlertEnabled,
    setExternalPrimaryTicketShopAlertEnabled,
  ] = useState(null)
  const showExternalTicketShopToggle = Boolean(
    externalPrimaryTicketShop && externalPrimaryTicketShop.state === 'CREATED'
  )
  const externalPrimaryTicketShopDateAnnounced = Boolean(externalPrimaryTicketShop?.startDate)

  const { show, hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: on => {
      if (!on) {
        const payload = types?.current?.map(node => ({
          eventTypeId: node.id,
          active: node.active,
        }))

        if (showExternalTicketShopToggle) {
          return onClose(payload, externalPrimaryTicketShopAlertEnabled)
        }

        onClose(payload)
      }
    },
  })

  /**
   * Determines whether the toggle is initially on or off.
   */
  function getOn(node) {
    // Filters
    const eventType = ({ eventType }) => eventType?.id === node.id
    const eventWithoutEventType = ({ event, eventType }) => event.id === eventId && !eventType

    // Check if alerts array contains the current event-type.
    if (alerts.filter(eventType).length > 0) {
      return true
    }

    // Check if alerts array contains the current event without a
    // type, which implies that alerts are enabled on event level.
    if (alerts.filter(eventWithoutEventType).length > 0) {
      return true
    }

    return false
  }

  function handleToggle(on, node) {
    // Find the correct type in the array and change
    // its `active` value.
    let newTypes = types.current
    for (const i in newTypes) {
      if (newTypes[i].id === node.id) {
        newTypes[i].active = on
        break // Stop loop once we found it.
      }
    }
    types.current = newTypes

    // Pass data for tracking-purposes.
    props.onToggle(on, 'EDIT_TYPES', {
      eventTypeId: decodeGlobalId(node.id),
      eventTypeName: node.title,
      isExternalPrimaryTicketshopAlertToggle: false,
    })
  }

  /*
    The actual alerts are getting disabled at the
    onToggle sideEffect
  */
  const setAlertsToDisabledAndHideDialog = () => {
    let newTypes = types.current

    for (const i in newTypes) {
      newTypes[i].active = false
    }
    types.current = newTypes

    setExternalPrimaryTicketShopAlertEnabled(false)

    props.onToggle(false)

    hide()
  }

  /*
    It's possbile that the component is mounted before the
    EPTS alert mutation is finished, and because of that the
    default value of EPTSToggle can be incorrect. This
    useEffect evaluate the state and will replace
    the default value.
  */
  useEffect(() => {
    if (externalPrimaryTicketShopAlerts.length) {
      setExternalPrimaryTicketShopAlertEnabled(true)
    } else {
      setExternalPrimaryTicketShopAlertEnabled(false)
    }
  }, [externalPrimaryTicketShopAlerts])

  const hasGroups = Boolean(props.types?.groups?.length)
  const hasNonEntrance = Boolean(props.types?.nonEntrance?.length)

  return (
    <>
      {props.children({ getToggleProps, show })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {t('event-eventtype:ticketAlerts.title')}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <StyledDialogBody>
          {showExternalTicketShopToggle && (
            <>
              <EventTypesHeader>
                <H6>{t('event-eventtype:ticketAlerts.sectionHeaders.organizer')}</H6>
              </EventTypesHeader>

              <ExternalPrimaryTicketShopBody>
                <ExternalPrimaryTicketShopText>
                  <Text as="p">
                    {t('event-eventtype:ticketAlerts.externalPrimaryTicketShop.title')}
                  </Text>
                  <Text>
                    {externalPrimaryTicketShopDateAnnounced
                      ? t(
                          'event-eventtype:ticketAlerts.externalPrimaryTicketShop.textDateAnnounced'
                        )
                      : t('event-eventtype:ticketAlerts.externalPrimaryTicketShop.text')}
                  </Text>
                </ExternalPrimaryTicketShopText>

                <ExternalPrimaryTicketShopToggle>
                  <Toggle
                    defaultOn={externalPrimaryTicketShopAlertEnabled}
                    adornment={<Alert size={16} />}
                    onToggle={on => {
                      setExternalPrimaryTicketShopAlertEnabled(on)
                      props.onToggle(on, 'EDIT_TYPES', {
                        isExternalPrimaryTicketshopAlertToggle: true,
                      })
                    }}
                  />
                </ExternalPrimaryTicketShopToggle>
              </ExternalPrimaryTicketShopBody>
            </>
          )}

          <EventTypesHeader>
            <H6>{t('event-eventtype:ticketAlerts.sectionHeaders.entrance')}</H6>
          </EventTypesHeader>
          <EventTypesBody>
            {props.types?.entrance?.map(({ node }) => (
              <EventTypeRow key={node.id}>
                <span>{node.title}</span>
                <Toggle
                  defaultOn={getOn(node)}
                  adornment={<Alert size={16} />}
                  onToggle={on => handleToggle(on, node)}
                />
              </EventTypeRow>
            ))}
          </EventTypesBody>

          {(hasGroups || hasNonEntrance) && (
            <>
              <EventTypesHeader>
                <H6>{t('event-eventtype:ticketAlerts.sectionHeaders.nonEntrance')}</H6>
              </EventTypesHeader>

              <EventTypesBody>
                <StyledAccordion collapsible={true}>
                  {hasGroups
                    ? props.types.groups.map(group => (
                        <StyledAccordionItem key={group.id}>
                          <AccordionButton>{group.name}</AccordionButton>
                          <AccordionPanel>
                            {group.eventTypes?.map(node => (
                              <EventTypeRow key={node.id}>
                                <span>{node.title}</span>
                                <Toggle
                                  defaultOn={getOn(node)}
                                  adornment={<Alert size={16} />}
                                  onToggle={on => handleToggle(on, node)}
                                />
                              </EventTypeRow>
                            ))}
                          </AccordionPanel>
                        </StyledAccordionItem>
                      ))
                    : null}

                  {hasNonEntrance ? (
                    <StyledAccordionItem>
                      <AccordionButton>{t('event-eventtype:types.nonEntrance')}</AccordionButton>
                      <AccordionPanel>
                        {props.types.nonEntrance.map(({ node }) => (
                          <EventTypeRow key={node.id}>
                            <span>{node.title}</span>
                            <Toggle
                              defaultOn={getOn(node)}
                              adornment={<Alert size={16} />}
                              onToggle={on => handleToggle(on, node)}
                            />
                          </EventTypeRow>
                        ))}
                      </AccordionPanel>
                    </StyledAccordionItem>
                  ) : null}
                </StyledAccordion>
              </EventTypesBody>
            </>
          )}
        </StyledDialogBody>
        <DialogFooter>
          <Button variant="caution" fullWidth onClick={() => setAlertsToDisabledAndHideDialog()}>
            {t('event-eventtype:ticketAlerts.action.disable')}
          </Button>
        </DialogFooter>
      </DialogWindow>
    </>
  )
}

TicketAlertsDialog.defaultProps = {
  alerts: [],
  types: {
    entrance: [],
    nonEntrance: [],
    groups: [],
  },
  onClose: () => {},
  onToggle: () => {},
}

TicketAlertsDialog.propTypes = {
  eventId: PropTypes.string.isRequired,
  alerts: PropTypes.array.isRequired,
  types: PropTypes.shape({
    entrance: PropTypes.array,
    nonEntrance: PropTypes.array,
    groups: PropTypes.array,
  }).isRequired,
  onClose: PropTypes.func,
  onToggle: PropTypes.func,
  externalPrimaryTicketShop: externalPrimaryTicketShopFragmentPropTypes,
  externalPrimaryTicketShopAlerts: externalPrimaryTicketShopAlertsPropTypes,
}

export default TicketAlertsDialog
